import React, { useState, useEffect } from 'react';
import { 
  Smartphone, 
  Download, 
  Wifi, 
  WifiOff, 
  Bell, 
  Settings, 
  Share2, 
  Camera,
  Mic,
  MapPin,
  Clock,
  Battery,
  Signal,
  CheckCircle,
  XCircle,
  AlertCircle,
  RefreshCw,
  Play,
  Pause,
  Square,
  Volume2,
  VolumeX,
  Sun,
  Moon,
  Monitor,
  Tablet,
  Phone,
  Watch,
  Headphones,
  Bluetooth,
  Zap,
  BarChart3
} from 'lucide-react';

interface PWAFeature {
  id: string;
  name: string;
  description: string;
  category: 'offline' | 'notifications' | 'media' | 'location' | 'device' | 'performance';
  status: 'enabled' | 'disabled' | 'available' | 'unavailable';
  icon: React.ComponentType<any>;
  settings: {
    autoSync: boolean;
    backgroundSync: boolean;
    pushNotifications: boolean;
    locationAccess: boolean;
    cameraAccess: boolean;
    microphoneAccess: boolean;
  };
  lastUsed?: Date;
  usageStats: {
    timesUsed: number;
    dataSaved: number; // in MB
    timeSaved: number; // in minutes
  };
}

interface PWAFeaturesProps {
  onBack?: () => void;
}

export const PWAFeatures: React.FC<PWAFeaturesProps> = ({ onBack }) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'features' | 'settings' | 'analytics'>('overview');
  const [features, setFeatures] = useState<PWAFeature[]>([]);
  const [selectedFeature, setSelectedFeature] = useState<PWAFeature | null>(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [batteryLevel, setBatteryLevel] = useState<number | null>(null);
  const [deviceInfo, setDeviceInfo] = useState<any>(null);

  // Mock PWA features data
  useEffect(() => {
    const mockFeatures: PWAFeature[] = [
      {
        id: '1',
        name: 'Offline Health Tracking',
        description: 'Track your health data even when offline, syncs when connection is restored',
        category: 'offline',
        status: 'enabled',
        icon: WifiOff,
        settings: {
          autoSync: true,
          backgroundSync: true,
          pushNotifications: true,
          locationAccess: false,
          cameraAccess: false,
          microphoneAccess: false
        },
        lastUsed: new Date(),
        usageStats: {
          timesUsed: 47,
          dataSaved: 156,
          timeSaved: 89
        }
      },
      {
        id: '2',
        name: 'Smart Notifications',
        description: 'Intelligent health reminders and alerts based on your patterns',
        category: 'notifications',
        status: 'enabled',
        icon: Bell,
        settings: {
          autoSync: true,
          backgroundSync: true,
          pushNotifications: true,
          locationAccess: false,
          cameraAccess: false,
          microphoneAccess: false
        },
        lastUsed: new Date('2025-01-14'),
        usageStats: {
          timesUsed: 23,
          dataSaved: 12,
          timeSaved: 45
        }
      },
      {
        id: '3',
        name: 'Voice Health Assistant',
        description: 'Voice-controlled health tracking and medication reminders',
        category: 'media',
        status: 'available',
        icon: Mic,
        settings: {
          autoSync: true,
          backgroundSync: false,
          pushNotifications: true,
          locationAccess: false,
          cameraAccess: false,
          microphoneAccess: true
        },
        usageStats: {
          timesUsed: 8,
          dataSaved: 34,
          timeSaved: 23
        }
      },
      {
        id: '4',
        name: 'Location-Based Health',
        description: 'Health insights based on your location and environmental factors',
        category: 'location',
        status: 'disabled',
        icon: MapPin,
        settings: {
          autoSync: true,
          backgroundSync: true,
          pushNotifications: true,
          locationAccess: true,
          cameraAccess: false,
          microphoneAccess: false
        },
        usageStats: {
          timesUsed: 0,
          dataSaved: 0,
          timeSaved: 0
        }
      },
      {
        id: '5',
        name: 'Camera Health Scanner',
        description: 'Scan food labels, medication bottles, and health documents',
        category: 'media',
        status: 'available',
        icon: Camera,
        settings: {
          autoSync: true,
          backgroundSync: false,
          pushNotifications: false,
          locationAccess: false,
          cameraAccess: true,
          microphoneAccess: false
        },
        lastUsed: new Date('2025-01-12'),
        usageStats: {
          timesUsed: 15,
          dataSaved: 78,
          timeSaved: 67
        }
      },
      {
        id: '6',
        name: 'Performance Optimization',
        description: 'Automatic performance tuning for smooth health tracking',
        category: 'performance',
        status: 'enabled',
        icon: Zap,
        settings: {
          autoSync: true,
          backgroundSync: true,
          pushNotifications: true,
          locationAccess: false,
          cameraAccess: false,
          microphoneAccess: false
        },
        usageStats: {
          timesUsed: 156,
          dataSaved: 234,
          timeSaved: 123
        }
      }
    ];
    setFeatures(mockFeatures);
  }, []);

  // Get device information
  useEffect(() => {
    const getDeviceInfo = () => {
      const info = {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language,
        cookieEnabled: navigator.cookieEnabled,
        onLine: navigator.onLine,
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight,
        devicePixelRatio: window.devicePixelRatio,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      };
      setDeviceInfo(info);
    };

    getDeviceInfo();

    // Listen for online/offline events
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Get battery level if available
  useEffect(() => {
    const getBatteryLevel = async () => {
      if ('getBattery' in navigator) {
        try {
          const battery = await (navigator as any).getBattery();
          setBatteryLevel(battery.level * 100);
          
          battery.addEventListener('levelchange', () => {
            setBatteryLevel(battery.level * 100);
          });
        } catch (error) {
          console.log('Battery API not supported');
        }
      }
    };

    getBatteryLevel();
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'enabled': return 'bg-green-100 text-green-800';
      case 'disabled': return 'bg-red-100 text-red-800';
      case 'available': return 'bg-blue-100 text-blue-800';
      case 'unavailable': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'offline': return 'bg-purple-100 text-purple-800';
      case 'notifications': return 'bg-orange-100 text-orange-800';
      case 'media': return 'bg-pink-100 text-pink-800';
      case 'location': return 'bg-green-100 text-green-800';
      case 'device': return 'bg-blue-100 text-blue-800';
      case 'performance': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const featureStats = {
    total: features.length,
    enabled: features.filter(f => f.status === 'enabled').length,
    available: features.filter(f => f.status === 'available').length,
    disabled: features.filter(f => f.status === 'disabled').length,
    totalUsage: features.reduce((sum, f) => sum + f.usageStats.timesUsed, 0),
    totalDataSaved: features.reduce((sum, f) => sum + f.usageStats.dataSaved, 0),
    totalTimeSaved: features.reduce((sum, f) => sum + f.usageStats.timeSaved, 0)
  };

  const getDeviceIcon = () => {
    const width = window.innerWidth;
    if (width < 768) return <Phone className="h-6 w-6" />;
    if (width < 1024) return <Tablet className="h-6 w-6" />;
    return <Monitor className="h-6 w-6" />;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">PWA Features</h1>
          <p className="text-slate-600">Progressive Web App features for mobile-first experience</p>
        </div>
        {onBack && (
          <button
            onClick={onBack}
            className="bg-slate-100 hover:bg-slate-200 text-slate-700 px-4 py-2 rounded-lg transition-colors"
          >
            ← Back
          </button>
        )}
      </div>

      {/* Device Status */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Connection</p>
              <p className="text-lg font-semibold text-slate-800">
                {isOnline ? 'Online' : 'Offline'}
              </p>
            </div>
            {isOnline ? (
              <Wifi className="h-8 w-8 text-green-500" />
            ) : (
              <WifiOff className="h-8 w-8 text-red-500" />
            )}
          </div>
        </div>
        
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Device</p>
              <p className="text-lg font-semibold text-slate-800">
                {deviceInfo?.viewportWidth && deviceInfo?.viewportHeight 
                  ? `${deviceInfo.viewportWidth}x${deviceInfo.viewportHeight}`
                  : 'Unknown'
                }
              </p>
            </div>
            {getDeviceIcon()}
          </div>
        </div>
        
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Battery</p>
              <p className="text-lg font-semibold text-slate-800">
                {batteryLevel ? `${Math.round(batteryLevel)}%` : 'Unknown'}
              </p>
            </div>
            <Battery className="h-8 w-8 text-blue-500" />
          </div>
        </div>
        
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Features</p>
              <p className="text-lg font-semibold text-slate-800">
                {featureStats.enabled}/{featureStats.total}
              </p>
            </div>
            <Smartphone className="h-8 w-8 text-purple-500" />
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="border-b border-slate-200">
          <nav className="flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'overview'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('features')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'features'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              Features
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'settings'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              Settings
            </button>
            <button
              onClick={() => setActiveTab('analytics')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'analytics'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              Analytics
            </button>
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-slate-800">PWA Overview</h3>
              
              {/* Quick Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg p-6 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-purple-100">Data Saved</p>
                      <p className="text-3xl font-bold">{featureStats.totalDataSaved} MB</p>
                    </div>
                    <Download className="h-12 w-12 text-purple-200" />
                  </div>
                </div>
                
                <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg p-6 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-green-100">Time Saved</p>
                      <p className="text-3xl font-bold">{featureStats.totalTimeSaved} min</p>
                    </div>
                    <Clock className="h-12 w-12 text-green-200" />
                  </div>
                </div>
                
                <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-6 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-blue-100">Features Used</p>
                      <p className="text-3xl font-bold">{featureStats.totalUsage}</p>
                    </div>
                    <Zap className="h-12 w-12 text-blue-200" />
                  </div>
                </div>
              </div>

              {/* Recent Activity */}
              <div>
                <h4 className="font-semibold text-slate-800 mb-4">Recent Activity</h4>
                <div className="space-y-3">
                  {features
                    .filter(f => f.lastUsed)
                    .sort((a, b) => (b.lastUsed?.getTime() || 0) - (a.lastUsed?.getTime() || 0))
                    .slice(0, 3)
                    .map((feature) => (
                      <div key={feature.id} className="bg-slate-50 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <feature.icon className="h-5 w-5 text-slate-600" />
                            <div>
                              <h5 className="font-medium text-slate-800">{feature.name}</h5>
                              <p className="text-sm text-slate-600">
                                Last used: {feature.lastUsed?.toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(feature.status)}`}>
                            {feature.status}
                          </span>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'features' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-slate-800">Available Features</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {features.map((feature) => (
                  <div key={feature.id} className="bg-white border border-slate-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <feature.icon className="h-6 w-6 text-slate-600" />
                        <div>
                          <h4 className="font-semibold text-slate-800">{feature.name}</h4>
                          <p className="text-sm text-slate-600">{feature.description}</p>
                        </div>
                      </div>
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(feature.status)}`}>
                        {feature.status}
                      </span>
                    </div>
                    
                    <div className="space-y-3">
                      <span className={`px-2 py-1 text-xs rounded-full ${getCategoryColor(feature.category)}`}>
                        {feature.category}
                      </span>
                      
                      <div className="grid grid-cols-3 gap-2 text-xs text-slate-500">
                        <div>
                          <p>Used</p>
                          <p className="font-semibold">{feature.usageStats.timesUsed} times</p>
                        </div>
                        <div>
                          <p>Data Saved</p>
                          <p className="font-semibold">{feature.usageStats.dataSaved} MB</p>
                        </div>
                        <div>
                          <p>Time Saved</p>
                          <p className="font-semibold">{feature.usageStats.timeSaved} min</p>
                        </div>
                      </div>
                      
                      <div className="flex space-x-2">
                        {feature.status === 'available' && (
                          <button className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm transition-colors">
                            Enable
                          </button>
                        )}
                        {feature.status === 'enabled' && (
                          <button className="bg-slate-600 hover:bg-slate-700 text-white px-3 py-1 rounded text-sm transition-colors">
                            Configure
                          </button>
                        )}
                        <button className="bg-slate-100 hover:bg-slate-200 text-slate-700 px-3 py-1 rounded text-sm transition-colors">
                          Details
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-slate-800">PWA Settings</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white rounded-lg p-6 border border-slate-200">
                  <h4 className="font-medium text-slate-800 mb-4">General Settings</h4>
                  <div className="space-y-4">
                    <label className="flex items-center space-x-2">
                      <input type="checkbox" defaultChecked className="rounded" />
                      <span className="text-sm text-slate-700">Auto-sync when online</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input type="checkbox" defaultChecked className="rounded" />
                      <span className="text-sm text-slate-700">Background sync</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input type="checkbox" defaultChecked className="rounded" />
                      <span className="text-sm text-slate-700">Push notifications</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input type="checkbox" className="rounded" />
                      <span className="text-sm text-slate-700">Location services</span>
                    </label>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-6 border border-slate-200">
                  <h4 className="font-medium text-slate-800 mb-4">Media Permissions</h4>
                  <div className="space-y-4">
                    <label className="flex items-center space-x-2">
                      <input type="checkbox" className="rounded" />
                      <span className="text-sm text-slate-700">Camera access</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input type="checkbox" className="rounded" />
                      <span className="text-sm text-slate-700">Mic access</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input type="checkbox" defaultChecked className="rounded" />
                      <span className="text-sm text-slate-700">Audio playback</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input type="checkbox" defaultChecked className="rounded" />
                      <span className="text-sm text-slate-700">Vibration feedback</span>
                    </label>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-6 border border-slate-200">
                  <h4 className="font-medium text-slate-800 mb-4">Performance</h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Cache Size</label>
                      <select className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                        <option value="100">100 MB</option>
                        <option value="250" selected>250 MB</option>
                        <option value="500">500 MB</option>
                        <option value="1000">1 GB</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Sync Frequency</label>
                      <select className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                        <option value="immediate">Immediate</option>
                        <option value="5min">Every 5 minutes</option>
                        <option value="15min" selected>Every 15 minutes</option>
                        <option value="1hour">Every hour</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-6 border border-slate-200">
                  <h4 className="font-medium text-slate-800 mb-4">Appearance</h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Theme</label>
                      <div className="flex space-x-2">
                        <button className="p-2 bg-slate-100 rounded-lg">
                          <Sun className="h-5 w-5 text-slate-600" />
                        </button>
                        <button className="p-2 bg-slate-800 rounded-lg">
                          <Moon className="h-5 w-5 text-white" />
                        </button>
                        <button className="p-2 bg-slate-100 rounded-lg">
                          <Monitor className="h-5 w-5 text-slate-600" />
                        </button>
                      </div>
                    </div>
                    
                    <label className="flex items-center space-x-2">
                      <input type="checkbox" defaultChecked className="rounded" />
                      <span className="text-sm text-slate-700">Reduce motion</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input type="checkbox" className="rounded" />
                      <span className="text-sm text-slate-700">High contrast</span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="flex space-x-4">
                <button className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors">
                  Save Settings
                </button>
                <button className="bg-slate-100 hover:bg-slate-200 text-slate-700 px-6 py-2 rounded-lg transition-colors">
                  Reset to Default
                </button>
              </div>
            </div>
          )}

          {activeTab === 'analytics' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-slate-800">Usage Analytics</h3>
              
              {/* Analytics Overview */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-lg p-4 border border-slate-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600">Total Usage</p>
                      <p className="text-2xl font-bold text-slate-800">{featureStats.totalUsage}</p>
                    </div>
                    <Zap className="h-8 w-8 text-blue-500" />
                  </div>
                </div>
                <div className="bg-white rounded-lg p-4 border border-slate-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600">Data Saved</p>
                      <p className="text-2xl font-bold text-slate-800">{featureStats.totalDataSaved} MB</p>
                    </div>
                    <Download className="h-8 w-8 text-green-500" />
                  </div>
                </div>
                <div className="bg-white rounded-lg p-4 border border-slate-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600">Time Saved</p>
                      <p className="text-2xl font-bold text-slate-800">{featureStats.totalTimeSaved} min</p>
                    </div>
                    <Clock className="h-8 w-8 text-purple-500" />
                  </div>
                </div>
                <div className="bg-white rounded-lg p-4 border border-slate-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600">Active Features</p>
                      <p className="text-2xl font-bold text-slate-800">{featureStats.enabled}</p>
                    </div>
                    <CheckCircle className="h-8 w-8 text-green-500" />
                  </div>
                </div>
              </div>

              {/* Usage Chart Placeholder */}
              <div className="bg-white rounded-lg p-6 border border-slate-200">
                <h4 className="font-semibold text-slate-800 mb-4">Feature Usage Over Time</h4>
                <div className="h-64 bg-slate-50 rounded-lg flex items-center justify-center">
                  <BarChart3 className="h-12 w-12 text-slate-400" />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}; 